/**
 * The landing page's رنگ‌بندی — three colours the clinic chooses, and the
 * contrast rules the SiteContent domain holds them to.
 *
 * These hex values are **content**, not this app's styling: they are the
 * landing's own palette and the reference text colours its template draws on
 * them, mirrored from the domain so the editor can say «خوانا نیست» before a
 * round trip. The panel itself never paints with them except in the sample.
 * The server is authoritative; its `site.landing_color_contrast` wins.
 */

export interface LandingTheme {
  readonly primaryColor: string;
  readonly backgroundColor: string;
  readonly footerColor: string;
}

export type LandingThemeField = keyof LandingTheme;

export const LANDING_THEME_FIELDS: readonly LandingThemeField[] = [
  'primaryColor',
  'backgroundColor',
  'footerColor',
];

/** Today's design — what a document without a `theme` reads as. */
export const DEFAULT_LANDING_THEME: LandingTheme = {
  // checker-ignore: design.no-raw-hex — the landing's default brand colour, content not styling
  primaryColor: '#04ABCD',
  // checker-ignore: design.no-raw-hex — the landing's default cream ground, content not styling
  backgroundColor: '#FBF7EF',
  // checker-ignore: design.no-raw-hex — the landing's default deep footer, content not styling
  footerColor: '#0C3B46',
};

/** The text colours the landing template draws on these surfaces — the domain's references. */
export const LANDING_TEXT_COLORS = {
  // checker-ignore: design.no-raw-hex — the landing template's body ink, mirrored from the domain
  ink: '#17303A',
  // checker-ignore: design.no-raw-hex — the landing template's secondary text, mirrored from the domain
  secondary: '#4A5F66',
  // checker-ignore: design.no-raw-hex — the landing template's footer text, mirrored from the domain
  cream: '#FBF7EF',
  // checker-ignore: design.no-raw-hex — dark label candidate on a primary fill, mirrored from the domain
  onPrimaryDark: '#062C36',
  // checker-ignore: design.no-raw-hex — light label candidate on a primary fill, mirrored from the domain
  onPrimaryLight: '#FFFFFF',
} as const;

/** A few tried starting points per field; the first of each is the default. */
export const LANDING_THEME_PRESETS: Readonly<Record<LandingThemeField, readonly string[]>> = {
  // checker-ignore: design.no-raw-hex — landing colour presets offered to the clinic, content not styling
  primaryColor: ['#04ABCD', '#0F766E', '#2563EB', '#7C3AED', '#BE185D'],
  // checker-ignore: design.no-raw-hex — landing colour presets offered to the clinic, content not styling
  backgroundColor: ['#FBF7EF', '#FFFFFF', '#F5FAFB', '#F7F5FF', '#FDF6F0'],
  // checker-ignore: design.no-raw-hex — landing colour presets offered to the clinic, content not styling
  footerColor: ['#0C3B46', '#17303A', '#1E293B', '#3B0764', '#14532D'],
};

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** Six-digit hex only — the domain's one accepted form. */
export function isHexColor(value: string): boolean {
  return HEX.test(value);
}

/** Upper case, as the domain stores it. */
export function normalizeHex(value: string): string {
  return value.trim().toUpperCase();
}

function channel(value: number): number {
  const srgb = value / 255;

  return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
}

/** WCAG 2 relative luminance of a `#RRGGBB` colour. */
export function relativeLuminance(hex: string): number {
  const red = Number.parseInt(hex.slice(1, 3), 16);
  const green = Number.parseInt(hex.slice(3, 5), 16);
  const blue = Number.parseInt(hex.slice(5, 7), 16);

  return 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue);
}

/** WCAG 2 contrast ratio between two `#RRGGBB` colours, 1–21. */
export function contrastRatio(first: string, second: string): number {
  const a = relativeLuminance(first);
  const b = relativeLuminance(second);

  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** The label drawn on a primary fill: whichever candidate contrasts more. */
export function onPrimaryColor(primary: string): string {
  const { onPrimaryDark, onPrimaryLight } = LANDING_TEXT_COLORS;

  return contrastRatio(onPrimaryDark, primary) >= contrastRatio(onPrimaryLight, primary)
    ? onPrimaryDark
    : onPrimaryLight;
}

export interface ContrastCheck {
  readonly passes: boolean;
  /** The ratio that decided it — the failing one when there are two. */
  readonly ratio: number;
  readonly required: number;
}

/**
 * The domain's rule for one field, on a valid hex colour:
 *
 * - background: body ink ≥ 7 **and** secondary text ≥ 4.5
 * - footer: cream text ≥ 4.5
 * - primary: the better of the two label colours ≥ 4.5
 */
export function checkThemeColor(field: LandingThemeField, hex: string): ContrastCheck {
  const { ink, secondary, cream } = LANDING_TEXT_COLORS;

  switch (field) {
    case 'backgroundColor': {
      const inkRatio = contrastRatio(ink, hex);

      if (inkRatio < 7) {
        return { passes: false, ratio: inkRatio, required: 7 };
      }

      const secondaryRatio = contrastRatio(secondary, hex);

      return { passes: secondaryRatio >= 4.5, ratio: secondaryRatio, required: 4.5 };
    }
    case 'footerColor': {
      const ratio = contrastRatio(cream, hex);

      return { passes: ratio >= 4.5, ratio, required: 4.5 };
    }
    case 'primaryColor': {
      const ratio = contrastRatio(onPrimaryColor(hex), hex);

      return { passes: ratio >= 4.5, ratio, required: 4.5 };
    }
  }
}

/** A ratio as the domain reports it: one decimal, rounded down so 4.49 never reads as 4.5. */
export function formatRatio(ratio: number): string {
  return (Math.floor(ratio * 10) / 10).toFixed(1);
}
